import { computed, onScopeDispose, ref, watch } from 'vue';
import type { Ref } from 'vue';
import { StoreSetupService } from '@/api/services/store-setup.service';
import { SLUG_MIN } from '@/domain/store-setup/store-slug';
import type { ISlugQuery } from '@/interfaces/IStoreSetup';

export const SLUG_CHECK_DEBOUNCE_MS = 400;

/**
 * `idle`: nothing to check yet (no name). `short`: under 3 characters
 * (blocking). `invalid`: a reserved or malformed link. `unchecked`: the check
 * failed; it never blocks the create call.
 */
export type TSlugState =
  | 'idle'
  | 'checking'
  | 'available'
  | 'taken'
  | 'short'
  | 'invalid'
  | 'unchecked';

export interface ISlugAvailabilityInput {
  name: string;
  city: string | null;
  /** The link as shown (it follows the name until the owner edits it). */
  slug: string;
  /** The owner typed the link or adopted a suggestion. */
  edited: boolean;
}

export interface IUseSlugAvailability {
  state: Ref<TSlugState>;
  /** Free alternatives the server checked (at most 3). */
  suggestions: Ref<string[]>;
}

/** What to ask the server, or the state to show without asking. */
function slugQuery(input: ISlugAvailabilityInput): ISlugQuery | TSlugState {
  if (input.edited) {
    return input.slug.length < SLUG_MIN
      ? 'short'
      : { slug: input.slug, city: input.city };
  }
  const name = input.name.trim();
  // The server derives the link from the name exactly like the field does.
  return name ? { name, city: input.city } : 'idle';
}

/**
 * Availability of the store link in «Negocio» (plan §4.2, §10.4): checked
 * 400 ms after the last change, by name while the link follows the name and
 * by the link itself once edited; stale answers are dropped and a failed
 * check reads `unchecked`.
 */
export function useSlugAvailability(
  input: () => ISlugAvailabilityInput
): IUseSlugAvailability {
  const state = ref<TSlugState>('idle');
  const suggestions = ref<string[]>([]);
  let requestNo = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;

  const query = computed(() => slugQuery(input()));
  // A stable key: an unrelated re-render of the getter never re-checks.
  const queryKey = computed(() => JSON.stringify(query.value));

  async function check(q: ISlugQuery, current: number): Promise<void> {
    try {
      const status = await StoreSetupService.slug(q);
      if (disposed || current !== requestNo) return;
      suggestions.value = status.suggestions;
      if (!status.valid) state.value = 'invalid';
      else state.value = status.available ? 'available' : 'taken';
    } catch {
      if (disposed || current !== requestNo) return;
      suggestions.value = [];
      state.value = 'unchecked';
    }
  }

  function schedule(): void {
    if (timer !== null) clearTimeout(timer);
    timer = null;
    const current = ++requestNo;
    const q = query.value;
    if (typeof q === 'string') {
      state.value = q;
      suggestions.value = [];
      return;
    }
    state.value = 'checking';
    timer = setTimeout(() => {
      timer = null;
      void check(q, current);
    }, SLUG_CHECK_DEBOUNCE_MS);
  }

  watch(queryKey, schedule, { immediate: true });

  onScopeDispose(() => {
    disposed = true;
    if (timer !== null) clearTimeout(timer);
    timer = null;
  });

  return { state, suggestions };
}
